// GUARDA DA SESSÃO — a régua (lógica pura, sem Amplify, axios nem antd: o jest
// do CRA testa isto de verdade). Quem liga nos eventos é guardaSessaoInstalar.ts.
//
// Incidente de 07/10/2026: a sessão da Daniele parou de renovar às 08:57 e o
// navegador seguiu mandando o idToken VENCIDO em toda chamada. O API Gateway
// recusa (401 "The incoming token has expired") SEM o cabeçalho CORS, então o
// axios/fetch só vê "Network Error" — em tudo, por 20+ minutos, até sair e entrar.
// O Amplify só entrega token vencido quando acha que ele ainda vale: decide pelo
// relógio DO COMPUTADOR (Date.now() + clockDrift) — relógio que volta no tempo
// depois da última renovação faz o portal mandar token vencido sem pedir outro.
//
// A guarda não depende desse relógio:
//   1. renova a cada 30 min de tempo CORRIDO (performance.now, que não volta com
//      o relógio do Windows) — o token dura 60 min no servidor;
//   2. chamada à API que falha sem resposta (o 401 sem CORS) ou com 401 renova
//      — no máximo uma vez por minuto; se o token guardado já é outro, usa ele —
//      e repete UMA vez, só quando é seguro repetir (podeRepetir);
//   3. renovação recusada pelo Cognito (sessão de 30 dias acabou, revogada) vira
//      o aviso "sua sessão expirou, entre de novo", e não uma tela de erros.

/** Renovar quando passar disto desde a última renovação (tempo corrido). */
export const RENOVAR_A_CADA_MS = 30 * 60 * 1000;
/** De quanto em quanto tempo a guarda olha o relógio corrido. */
export const CHECAR_A_CADA_MS = 60 * 1000;
/** A renovação que não responde em tanto tempo é dada como falha de rede (o
 *  Amplify não tem timeout: um pedido pendurado prenderia todas as chamadas). */
export const LIMITE_RENOVACAO_MS = 15 * 1000;
/** Folga na conta "o token enviado já estava vencido no servidor". */
export const FOLGA_MS = 5 * 1000;
/** Entre duas renovações FORÇADAS, no mínimo isto (e depois de uma renovação
 *  sem resposta, também): numa pane da API, cada falha pedindo renovação viraria
 *  um laço — renovação → o portal recarrega o perfil → falha → renovação… */
export const INTERVALO_MINIMO_MS = 60 * 1000;
/** Erros sem resposta que NÃO são a sessão: demora (timeout) e cancelamento. */
const SEM_RESPOSTA_QUE_NAO_E_SESSAO = /^(ECONNABORTED|ETIMEDOUT|ERR_CANCELED|AbortError|TimeoutError)$/;

const METODOS_SEGUROS = ['get', 'head', 'options'];

export type ClaimsJwt = { exp?: number; iat?: number; [k: string]: unknown };

/** O payload de um JWT (base64url), ou null se não for um JWT legível. */
export function claimsDoJwt(token?: string | null): ClaimsJwt | null {
  const t = String(token || '').replace(/^Bearer\s+/i, '').trim();
  const partes = t.split('.');
  if (partes.length !== 3 || !partes[1]) return null;
  try {
    const b64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const c = JSON.parse(json);
    return c && typeof c === 'object' ? c : null;
  } catch {
    return null;
  }
}

/** A chamada é para a NOSSA API? (o Amplify fala com o Cognito por fetch — esse não se toca) */
export function ehDaApi(url: string | null | undefined, apiUrl: string): boolean {
  const u = String(url || '');
  const base = String(apiUrl || '').replace(/\/+$/, '');
  if (!u || !base) return false;
  return u === base || u.startsWith(base + '/') || u.startsWith(base + '?');
}

export type Falha = 'sessao' | 'talvez_sessao' | 'outra';

/** 401 = a sessão (o authorizer recusou); sem resposta = pode ser a sessão (o 401
 *  do API Gateway sai sem CORS e o navegador esconde) ou a rede; o resto não é. */
export function classificarFalha(o: { status?: number | null; temResposta: boolean; codigo?: string | null }): Falha {
  if (!o.temResposta) return SEM_RESPOSTA_QUE_NAO_E_SESSAO.test(String(o.codigo || '')) ? 'outra' : 'talvez_sessao';
  if (o.status === 401) return 'sessao';
  return 'outra';
}

/**
 * Depois de renovar, a chamada pode ser repetida?
 *
 * Repetir uma GRAVAÇÃO que chegou ao servidor faria a coisa duas vezes. Só é
 * seguro quando há certeza de que a primeira não passou:
 *   · leitura (GET/HEAD/OPTIONS): repetir não muda nada;
 *   · gravação (sem resposta ou com 401 — o 401 do authorizer chega sem CORS,
 *     então um 401 VISÍVEL vem de Lambda): só se o token enviado JÁ estava
 *     vencido no relógio do SERVIDOR quando a chamada saiu. A hora do servidor é
 *     o `iat` do token novo; o tempo desde o envio é o MAIOR entre o tempo
 *     corrido (que pode parar com o aparelho suspenso) e o relógio do computador
 *     (que pode pular) — o maior é o lado que nunca repete à toa.
 * Token novo vazio ou igual ao enviado: não há o que repetir.
 */
export function podeRepetir(o: {
  metodo?: string | null; status?: number | null; temResposta: boolean;
  tokenEnviado?: string | null; tokenNovo?: string | null; msDesdeEnvio: number;
}): boolean {
  const novo = String(o.tokenNovo || '');
  if (!novo || novo === String(o.tokenEnviado || '')) return false;
  if (o.temResposta && o.status !== 401) return false;   // 403, 500…: a sessão não era o problema
  if (METODOS_SEGUROS.includes(String(o.metodo || 'get').toLowerCase())) return true;
  const exp = claimsDoJwt(o.tokenEnviado)?.exp;
  const iat = claimsDoJwt(novo)?.iat;
  if (typeof exp !== 'number' || typeof iat !== 'number') return false;
  // hora do servidor quando a chamada saiu ≈ iat do token novo − o tempo corrido desde o envio
  const saiuEm = iat * 1000 - Math.max(0, o.msDesdeEnvio);
  return exp * 1000 + FOLGA_MS < saiuEm;
}

/** Tempo desde o envio, pelo lado seguro: o maior entre o corrido e o do relógio de parede. */
export function msDesdeEnvioSeguro(corrido: number, parede: number): number {
  return Math.max(0, Number.isFinite(corrido) ? corrido : 0, Number.isFinite(parede) ? parede : 0);
}

/** Pode forçar outra renovação agora? (no máximo uma por minuto, e não logo depois de uma sem resposta) */
export function podeForcarRenovacao(msDesdeUltimaForcada: number, msDesdeUltimoTempoEsgotado: number): boolean {
  return msDesdeUltimaForcada >= INTERVALO_MINIMO_MS && msDesdeUltimoTempoEsgotado >= INTERVALO_MINIMO_MS;
}

/** Hora de renovar pelo tempo corrido desde a última renovação? */
export function precisaRenovar(msDesdeUltimaRenovacao: number): boolean {
  return msDesdeUltimaRenovacao >= RENOVAR_A_CADA_MS;
}

/** A renovação foi RECUSADA pelo Cognito (acabou a sessão) — e não falha de rede? */
export function renovacaoRecusada(nomeDoErro?: string | null): boolean {
  return /^(NotAuthorizedException|TokenRevokedException|RefreshTokenReuseException|UserNotFoundException|PasswordResetRequiredException|UserNotConfirmedException)/
    .test(String(nomeDoErro || ''));
}

/** O cabeçalho Authorization de qualquer formato de headers (objeto, Headers, AxiosHeaders, lista). */
export function authorizationDe(headers: unknown): string {
  if (!headers) return '';
  const h: any = headers;
  if (typeof h.get === 'function') {
    try { const v = h.get('Authorization') ?? h.get('authorization'); if (v) return String(v); } catch { /* segue */ }
  }
  if (Array.isArray(h)) {
    const par = h.find((p: any) => Array.isArray(p) && String(p[0]).toLowerCase() === 'authorization');
    return par ? String(par[1] || '') : '';
  }
  if (typeof h === 'object') {
    for (const k of Object.keys(h)) if (k.toLowerCase() === 'authorization') return String(h[k] || '');
  }
  return '';
}

/** Mesmos headers com o Authorization trocado (sem mexer no original). */
export function comAuthorization(headers: unknown, token: string): any {
  const h: any = headers;
  if (h && typeof h.set === 'function' && typeof h.get === 'function' && !Array.isArray(h)) {
    // Headers do fetch (ou AxiosHeaders): copia e troca
    const copia: any = typeof Headers !== 'undefined' && h instanceof Headers ? new Headers(h) : h;
    copia.set('Authorization', token);
    return copia;
  }
  if (Array.isArray(h)) return [...h.filter((p: any) => String(p[0]).toLowerCase() !== 'authorization'), ['Authorization', token]];
  const base: Record<string, any> = {};
  if (h && typeof h === 'object') for (const k of Object.keys(h)) if (k.toLowerCase() !== 'authorization') base[k] = h[k];
  base.Authorization = token;
  return base;
}

/** A frase do aviso (sem jargão: quem lê não é desenvolvedor). */
export const AVISO_SESSAO = {
  titulo: 'Sua sessão expirou',
  texto: 'Por segurança, o acesso precisa ser renovado. Entre de novo para continuar — o que já foi salvo não se perde.',
  botao: 'Entrar de novo',
};
